import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, effect, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { filter } from 'rxjs';

import { AppLanguage, AppPreferences } from './app-preferences';

type SeoPageKey =
  | 'landing'
  | 'personalData'
  | 'workingData'
  | 'paymentInformation'
  | 'offerResult'
  | 'offerConfirmation'
  | 'fundRequestSuccessfully'
  | 'noOfferResult'
  | 'myRequests'
  | 'trackingOtp'
  | 'requestDetails';

interface SeoPageContent {
  title: string;
  description: string;
  keywords: string;
}

interface SeoRouteData {
  seoKey?: SeoPageKey;
  robots?: string;
}

const SITE_NAME = 'Finora';
const FALLBACK_SITE_URL = 'https://finora.sa';
const DEFAULT_IMAGE = '/assets/images/global/logo.webp';
const DEFAULT_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const PRIVATE_ROBOTS = 'noindex, follow';

const SEO_CONTENT: Record<AppLanguage, Record<SeoPageKey, SeoPageContent>> = {
  ar: {
    landing: {
      title: 'الوسيط | تمويل شخصي ومرابحة في السعودية',
      description:
        'قارن عروض التمويل الشخصي والمرابحة من جهات تمويل في السعودية، مع خيارات مناسبة للعملاء في الرياض وجدة وخطوات طلب سهلة أونلاين.',
      keywords:
        'تمويل شخصي, مرابحة, قروض السعودية, تمويل في الرياض, تمويل في جدة, قروض شخصية, عروض تمويل, بنوك السعودية, تمويل اسلامي',
    },
    personalData: {
      title: 'طلب تمويل شخصي | بيانات العميل',
      description:
        'ابدأ طلب تمويل شخصي أو مرابحة في السعودية بإدخال بياناتك الأساسية للحصول على عروض تمويل مناسبة.',
      keywords: 'طلب تمويل شخصي, تمويل مرابحة, قروض شخصية السعودية, تمويل الرياض, تمويل جدة',
    },
    workingData: {
      title: 'بيانات العمل | طلب تمويل شخصي',
      description:
        'أكمل بيانات العمل والراتب لمقارنة عروض التمويل الشخصي والمرابحة من جهات تمويل في السعودية.',
      keywords: 'بيانات الراتب, جهة العمل, تمويل شخصي السعودية, مرابحة, بنوك التمويل',
    },
    paymentInformation: {
      title: 'البيانات الائتمانية | عروض التمويل',
      description:
        'أدخل الالتزامات والبيانات الائتمانية لعرض خيارات تمويل شخصي ومرابحة مناسبة في السعودية.',
      keywords: 'الالتزامات الشهرية, عروض التمويل, تمويل شخصي, مرابحة السعودية, سمة',
    },
    offerResult: {
      title: 'عروض التمويل الشخصي | Finora',
      description:
        'استعرض عروض التمويل الشخصي والمرابحة المتاحة وقارن قيمة التمويل والقسط الشهري ومدة السداد.',
      keywords: 'عروض تمويل شخصي, أقل قسط شهري, أقصى قيمة تمويل, مرابحة, قروض السعودية',
    },
    offerConfirmation: {
      title: 'تأكيد عرض التمويل | Finora',
      description: 'راجع تفاصيل عرض التمويل الشخصي أو المرابحة المختار قبل تأكيد الطلب.',
      keywords: 'تأكيد عرض تمويل, تمويل شخصي, مرابحة, تفاصيل التمويل',
    },
    fundRequestSuccessfully: {
      title: 'تم تقديم طلبك بنجاح | Finora',
      description: 'تم استلام طلب التمويل الشخصي أو المرابحة وسنتواصل معك لاستكمال الطلب.',
      keywords: 'تم تقديم طلب تمويل, تمويل شخصي, مرابحة, طلب تمويل ناجح',
    },
    noOfferResult: {
      title: 'لا توجد عروض تمويل مناسبة | Finora',
      description:
        'لم يتم العثور على عروض تمويل مناسبة حاليا. يمكنك إعادة المحاولة لاحقا أو التواصل مع الدعم.',
      keywords: 'لا توجد عروض تمويل, تمويل شخصي السعودية, قروض شخصية, مرابحة',
    },
    myRequests: {
      title: 'طلباتي | Finora',
      description: 'تابع حالة طلبات التمويل الخاصة بك بعد التحقق من الهوية ورقم الجوال.',
      keywords: 'متابعة طلب تمويل, طلباتي, حالة طلب التمويل, Finora',
    },
    trackingOtp: {
      title: 'رمز التحقق | Finora',
      description: 'أدخل رمز التحقق لمتابعة حالة طلب التمويل الخاص بك.',
      keywords: 'رمز التحقق, متابعة طلب تمويل, حالة طلب التمويل, Finora',
    },
    requestDetails: {
      title: 'تفاصيل الطلب | Finora',
      description: 'راجع تفاصيل طلب التمويل والعرض المرتبط به.',
      keywords: 'تفاصيل طلب التمويل, حالة الطلب, عرض تمويل, Finora',
    },
  },
  en: {
    landing: {
      title: 'Finora | Personal Finance and Murabaha in Saudi Arabia',
      description:
        'Compare personal finance and Murabaha offers from financing providers in Saudi Arabia, with options for customers in Riyadh and Jeddah.',
      keywords:
        'personal finance Saudi Arabia, Murabaha finance, loans Saudi Arabia, Riyadh finance, Jeddah finance, personal loans, financing offers',
    },
    personalData: {
      title: 'Personal Finance Request | Customer Details',
      description:
        'Start a personal finance or Murabaha request in Saudi Arabia by entering your basic details.',
      keywords: 'personal finance request, Murabaha finance, Saudi personal loans, Riyadh finance, Jeddah finance',
    },
    workingData: {
      title: 'Employment Details | Personal Finance Request',
      description:
        'Complete employment and salary details to compare personal finance and Murabaha offers in Saudi Arabia.',
      keywords: 'salary details, employer sector, personal finance Saudi Arabia, Murabaha, financing providers',
    },
    paymentInformation: {
      title: 'Credit Information | Financing Offers',
      description:
        'Enter credit obligations to view suitable personal finance and Murabaha options in Saudi Arabia.',
      keywords: 'monthly obligations, financing offers, personal finance, Murabaha Saudi Arabia, SIMAH',
    },
    offerResult: {
      title: 'Personal Finance Offers | Finora',
      description:
        'Review available personal finance and Murabaha offers and compare finance amount, monthly installment, and duration.',
      keywords: 'personal finance offers, lowest monthly installment, max finance amount, Murabaha, Saudi loans',
    },
    offerConfirmation: {
      title: 'Confirm Financing Offer | Finora',
      description: 'Review your selected personal finance or Murabaha offer before confirming the request.',
      keywords: 'confirm financing offer, personal finance, Murabaha, finance details',
    },
    fundRequestSuccessfully: {
      title: 'Request Submitted Successfully | Finora',
      description:
        'Your personal finance or Murabaha request has been received and we will contact you to complete it.',
      keywords: 'finance request submitted, personal finance, Murabaha, successful finance request',
    },
    noOfferResult: {
      title: 'No Suitable Financing Offers | Finora',
      description:
        'No suitable financing offers were found right now. You can try again later or contact support.',
      keywords: 'no financing offers, personal finance Saudi Arabia, personal loans, Murabaha',
    },
    myRequests: {
      title: 'My Requests | Finora',
      description: 'Track your financing requests after verifying your ID and mobile number.',
      keywords: 'track financing request, my requests, financing request status, Finora',
    },
    trackingOtp: {
      title: 'Verification Code | Finora',
      description: 'Enter the verification code to track your financing request status.',
      keywords: 'verification code, track financing request, financing request status, Finora',
    },
    requestDetails: {
      title: 'Request Details | Finora',
      description: 'Review your financing request details and selected offer.',
      keywords: 'financing request details, request status, financing offer, Finora',
    },
  },
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly preferences = inject(AppPreferences);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.applyCurrentRouteSeo();
    });

    effect(() => {
      this.preferences.language();
      this.applyCurrentRouteSeo();
    });
  }

  private applyCurrentRouteSeo(): void {
    const routeData = this.getDeepestRouteData();
    const seoKey = routeData.seoKey ?? 'landing';
    const language = this.preferences.language();
    const content = SEO_CONTENT[language][seoKey];
    const url = this.getAbsoluteUrl(this.router.url.split('?')[0] || '/');
    const imageUrl = this.getAbsoluteUrl(DEFAULT_IMAGE);
    const locale = language === 'ar' ? 'ar_SA' : 'en_US';
    const alternateLocale = language === 'ar' ? 'en_US' : 'ar_SA';
    const robots = routeData.robots ?? DEFAULT_ROBOTS;

    this.title.setTitle(content.title);
    this.updateMeta('description', content.description);
    this.updateMeta('keywords', content.keywords);
    this.updateMeta('robots', robots);
    this.updateMeta('author', SITE_NAME);
    this.updateMeta('theme-color', '#602FA2');
    this.updateProperty('og:type', seoKey === 'landing' ? 'website' : 'article');
    this.updateProperty('og:site_name', SITE_NAME);
    this.updateProperty('og:title', content.title);
    this.updateProperty('og:description', content.description);
    this.updateProperty('og:url', url);
    this.updateProperty('og:image', imageUrl);
    this.updateProperty('og:locale', locale);
    this.updateProperty('og:locale:alternate', alternateLocale);
    this.updateMeta('twitter:card', 'summary_large_image');
    this.updateMeta('twitter:title', content.title);
    this.updateMeta('twitter:description', content.description);
    this.updateMeta('twitter:image', imageUrl);
    this.updateLink('canonical', url);
    this.updateAlternateLinks(url);
    this.updateStructuredData(seoKey, content, url, imageUrl, language);
  }

  private getDeepestRouteData(): SeoRouteData {
    let current = this.route;

    while (current.firstChild) {
      current = current.firstChild;
    }

    return current.snapshot.data as SeoRouteData;
  }

  private updateMeta(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private updateProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private updateLink(rel: string, href: string, hreflang?: string): void {
    const selector = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
    let link = this.document.head.querySelector<HTMLLinkElement>(selector);

    if (!link) {
      link = this.document.createElement('link');
      link.rel = rel;
      if (hreflang) {
        link.hreflang = hreflang;
      }
      this.document.head.appendChild(link);
    }

    link.href = href;
  }

  private updateAlternateLinks(url: string): void {
    this.updateLink('alternate', url, 'ar-SA');
    this.updateLink('alternate', url, 'en-SA');
    this.updateLink('alternate', url, 'x-default');
  }

  private updateStructuredData(
    seoKey: SeoPageKey,
    content: SeoPageContent,
    url: string,
    imageUrl: string,
    language: AppLanguage,
  ): void {
    const id = 'finora-seo-jsonld';
    let script = this.document.getElementById(id) as HTMLScriptElement | null;
    const graph: Record<string, unknown>[] = [
      {
        '@type': 'Organization',
        '@id': `${this.getSiteOrigin()}#organization`,
        name: SITE_NAME,
        url: this.getSiteOrigin(),
        logo: imageUrl,
        areaServed: ['Saudi Arabia', 'Riyadh', 'Jeddah'],
      },
      {
        '@type': 'FinancialService',
        '@id': `${this.getSiteOrigin()}#financial-service`,
        name: SITE_NAME,
        url: this.getSiteOrigin(),
        image: imageUrl,
        serviceType: language === 'ar' ? 'تمويل شخصي ومرابحة' : 'Personal finance and Murabaha',
        areaServed: [
          { '@type': 'Country', name: 'Saudi Arabia' },
          { '@type': 'City', name: 'Riyadh' },
          { '@type': 'City', name: 'Jeddah' },
        ],
        provider: { '@id': `${this.getSiteOrigin()}#organization` },
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: content.title,
        description: content.description,
        inLanguage: language === 'ar' ? 'ar-SA' : 'en-SA',
        isPartOf: { '@id': `${this.getSiteOrigin()}#website` },
        about: { '@id': `${this.getSiteOrigin()}#financial-service` },
      },
    ];

    if (seoKey === 'landing') {
      graph.push({
        '@type': 'WebSite',
        '@id': `${this.getSiteOrigin()}#website`,
        name: SITE_NAME,
        url: this.getSiteOrigin(),
        description: content.description,
        inLanguage: language === 'ar' ? 'ar-SA' : 'en-SA',
      });
    }

    if (!script) {
      script = this.document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': graph,
    });
  }

  private getAbsoluteUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const origin = this.getSiteOrigin();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;

    return `${origin}${normalizedPath}`;
  }

  private getSiteOrigin(): string {
    if (this.isBrowser && this.document.defaultView?.location.origin) {
      return this.document.defaultView.location.origin;
    }

    return FALLBACK_SITE_URL;
  }
}
